import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
} from "@angular/common/http";
import { inject } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, throwError } from "rxjs";
import { catchError, switchMap } from "rxjs/operators";
import { AuthService } from "../services/auth.service";

/** Marks a request that already retried after a token refresh (prevents refresh loops). */
const RETRY_HEADER = "X-Auth-Retry";

function isAuthExempt(url: string): boolean {
  return (
    url.includes("/auth/admin-login") ||
    url.includes("/auth/login") ||
    url.includes("/auth/refresh") ||
    url.includes("/auth/logout") ||
    url.includes("/auth/register")
  );
}

function addTokenToRequest(
  request: HttpRequest<unknown>,
  token: string,
): HttpRequest<unknown> {
  return request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
    },
  });
}

function forceLogoutToLogin(authService: AuthService, router: Router): void {
  authService.logout().subscribe({
    next: () => {
      void router.navigate(["/login"]);
    },
    error: () => {
      void router.navigate(["/login"]);
    },
  });
}

function refreshAndRetry(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
): Observable<HttpEvent<unknown>> {
  return authService.refreshToken().pipe(
    catchError((refreshError) => {
      forceLogoutToLogin(authService, router);
      return throwError(() => refreshError);
    }),
    switchMap((newToken: string) =>
      next(
        addTokenToRequest(request, newToken).clone({
          setHeaders: { [RETRY_HEADER]: "1" },
        }),
      ),
    ),
  );
}

function sendWithToken(
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  token: string | null,
  authService: AuthService,
  router: Router,
): Observable<HttpEvent<unknown>> {
  const alreadyRetried = request.headers.has(RETRY_HEADER);
  const authRequest = token ? addTokenToRequest(request, token) : request;

  return next(authRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status !== 401) {
        return throwError(() => error);
      }

      if (alreadyRetried || !authService.getRefreshToken()) {
        forceLogoutToLogin(authService, router);
        return throwError(() => error);
      }

      return refreshAndRetry(request, next, authService, router);
    }),
  );
}

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (isAuthExempt(request.url)) {
    return next(request);
  }

  const alreadyRetried = request.headers.has(RETRY_HEADER);

  if (
    !alreadyRetried &&
    authService.getRefreshToken() &&
    (authService.isTokenExpired() || !authService.getToken())
  ) {
    return refreshAndRetry(request, next, authService, router);
  }

  return sendWithToken(
    request,
    next,
    authService.getToken(),
    authService,
    router,
  );
};
